import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { HttpError, HttpResponseList } from '@shared/utils/models/http.model';
import { switchMap, catchError, of, map } from 'rxjs';
import { UtilityService } from '../services/utility.service';
import { UtilityActions, UtilityResetActions } from '../actions/utility.actions';
import {
  Activity,
  Addon,
  AffiliateAction,
  AffiliateType,
  ApplicantType,
  Bank,
  City,
  CityTree,
  Classification,
  ClassificationTree,
  ClassificationType,
  Intention,
  Interest,
  License,
  LinkType,
  OfferDemandIntention,
  Package,
  PaymentMethod,
  PropertyType,
  Province,
  PublicPropertyTypeClassification,
  Tag,
  Theme,
} from '@core/utils/models/utility.model';

@Injectable()
export class UtilityEffects {
  private readonly utilityService = inject(UtilityService);
  private readonly actions$ = inject(Actions);

  getBanks$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getBanks, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getBanks().pipe(
              map((response: HttpResponseList<Bank>) => UtilityActions.getBanksSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getBanksFailure({ error })))
            )
      )
    );
  });

  getPropertyTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getPropertyTypes, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getPropertyTypes().pipe(
              map((response: HttpResponseList<PropertyType>) =>
                UtilityActions.getPropertyTypesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getPropertyTypesFailure({ error })))
            )
      )
    );
  });

  getCities$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getCities, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getCities().pipe(
              map((response: HttpResponseList<City>) => UtilityActions.getCitiesSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getCitiesFailure({ error })))
            )
      )
    );
  });

  getCitiesTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getCitiesTree, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getCitiesTree().pipe(
              map((response: HttpResponseList<CityTree>) =>
                UtilityActions.getCitiesTreeSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getCitiesTreeFailure({ error })))
            )
      )
    );
  });

  getProvinces$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getProvinces, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getProvinces().pipe(
              map((response: HttpResponseList<Province>) =>
                UtilityActions.getProvincesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getProvincesFailure({ error })))
            )
      )
    );
  });

  getIntentions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getIntentions, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getIntentions().pipe(
              map((response: HttpResponseList<Intention>) =>
                UtilityActions.getIntentionsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getIntentionsFailure({ error })))
            )
      )
    );
  });

  getInterests$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getInterests, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getInterests().pipe(
              map((response: HttpResponseList<Interest>) =>
                UtilityActions.getInterestsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getInterestsFailure({ error })))
            )
      )
    );
  });

  getSocialMediaLinkTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getSocialMediaLinks, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getSocialMediaLinks().pipe(
              map((response: HttpResponseList<LinkType>) =>
                UtilityActions.getSocialMediaLinksSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getSocialMediaLinksFailure({ error })))
            )
      )
    );
  });

  getThemes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getThemes, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getThemes().pipe(
              map((response: HttpResponseList<Theme>) => UtilityActions.getThemesSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getThemesFailure({ error })))
            )
      )
    );
  });

  getClassifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getClassifications, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getClassifications().pipe(
              map((response: HttpResponseList<Classification>) =>
                UtilityActions.getClassificationsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getClassificationsFailure({ error })))
            )
      )
    );
  });

  getClassificationTree$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getClassificationTree, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getClassificationTree().pipe(
              map((response: HttpResponseList<ClassificationTree>) =>
                UtilityActions.getClassificationTreeSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getClassificationTreeFailure({ error })))
            )
      )
    );
  });

  getClassificationTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getClassificationTypes, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getClassificationTypes().pipe(
              map((response: HttpResponseList<ClassificationType>) =>
                UtilityActions.getClassificationTypesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getClassificationTypesFailure({ error })))
            )
      )
    );
  });

  getActivities = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getActivities, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getActivities().pipe(
              map((response: HttpResponseList<Activity>) =>
                UtilityActions.getActivitiesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getActivitiesFailure({ error })))
            )
      )
    );
  });

  getLicenses$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getLicenses, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getLicenses().pipe(
              map((response: HttpResponseList<License>) => UtilityActions.getLicensesSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getLicensesFailure({ error })))
            )
      )
    );
  });

  getAffiliateTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getAffiliateTypes, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getAffiliateTypes().pipe(
              map((response: HttpResponseList<AffiliateType>) =>
                UtilityActions.getAffiliateTypesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getAffiliateTypesFailure({ error })))
            )
      )
    );
  });

  getAffiliateActions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getAffiliateActions, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getAffiliateActions().pipe(
              map((response: HttpResponseList<AffiliateAction>) =>
                UtilityActions.getAffiliateActionsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getAffiliateActionsFailure({ error })))
            )
      )
    );
  });

  getAddons$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getAddons, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getAddons().pipe(
              map((response: HttpResponseList<Addon>) => UtilityActions.getAddonsSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getAddonsFailure({ error })))
            )
      )
    );
  });

  getPublicPropertyTypeClassifications$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getPublicPropertyTypeClassifications, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getPublicPropertyTypeClassifications().pipe(
              map((response: HttpResponseList<PublicPropertyTypeClassification>) =>
                UtilityActions.getPublicPropertyTypeClassificationsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) =>
                of(UtilityActions.getPublicPropertyTypeClassificationsFailure({ error }))
              )
            )
      )
    );
  });

  getPublicIntentions$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getPublicIntentions, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getPublicIntentions().pipe(
              map((response: HttpResponseList<OfferDemandIntention>) =>
                UtilityActions.getPublicIntentionsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getPublicIntentionsFailure({ error })))
            )
      )
    );
  });

  getTags$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getTags, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getTags().pipe(
              map((response: HttpResponseList<Tag>) => UtilityActions.getTagsSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getTagsFailure({ error })))
            )
      )
    );
  });

  getApplicantTypes$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getApplicantTypes, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getApplicantTypes().pipe(
              map((response: HttpResponseList<ApplicantType>) =>
                UtilityActions.getApplicantTypesSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getApplicantTypesFailure({ error })))
            )
      )
    );
  });

  getPaymentMethods$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getPaymentMethods, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getPaymentMethods().pipe(
              map((response: HttpResponseList<PaymentMethod>) =>
                UtilityActions.getPaymentMethodsSuccess({ data: response.data })
              ),
              catchError((error: HttpError) => of(UtilityActions.getPaymentMethodsFailure({ error })))
            )
      )
    );
  });

  getPackages$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(UtilityActions.getPackages, UtilityResetActions.cancelUtilityActions),
      switchMap((action) =>
        action.type === UtilityResetActions.cancelUtilityActions.type
          ? of(UtilityResetActions.cancelUtilityActionsSuccess())
          : this.utilityService.getPackages().pipe(
              map((response: HttpResponseList<Package>) => UtilityActions.getPackagesSuccess({ data: response.data })),
              catchError((error: HttpError) => of(UtilityActions.getPackagesFailure({ error })))
            )
      )
    );
  });
}
